import * as Astronomy from 'astronomy-engine';
import { LANG, HoroscopeLang, getLangDict } from './horoscope-lang';
import { generateCastingHTML } from './horoscope-pdf-template';

export interface HoroscopeResult {
  basicInfo: {
    name: string;
    date: string;
    time: string;
    place: string;
    latitude: number;
    longitude: number;
    timezone: number;
  };
  planetData: Record<string, any>;
  siderealPositions: Record<string, number>;
  dasaInfo: any;
  ashtaka: Record<string, any>;
  bhavas: any;
  bhavaPlanets: Record<number, string[]>;
  vargasTextTable: Record<string, any>;
  vargas: Record<string, any>;
  thithi: { paksha: string; name: string };
  yoga: { name: string };
  karana: { name: string };
  birthNak: { name: string; pada: number };
  tamilYear?: string;
  tamilYearIdx?: number;
  tamilMonth?: string;
  tamilMonthIdx?: number;
  tamilDay?: number;
  formattedOutput: string;
  htmlOutput?: string;
}

export interface BirthDetails {
  name: string;
  date: string;
  time: string;
  place: string;
  latitude: number;
  longitude: number;
  timezone: number;
}

const NAKSHATRAS = [
    'ASWINI', 'BHARANI', 'KRITHIKA', 'ROHINI', 'MRIGASIR', 'ARDRA', 'PUNARVAS',
    'PUSHYAMI', 'ASHLESHA', 'MAKHA', 'PUBBA', 'UTTARA', 'HASTA', 'CHITRA',
    'SWATHI', 'VISAKHA', 'ANURADHA', 'JYESHTA', 'MOOLA', 'PURVASHD', 'UTRASADA',
    'SHRAVANA', 'DHANISTA', 'SATABISA', 'PURVBDRA', 'UTRABDRA', 'REVATHI'
];

const RASIS = [
    'MESHA', 'RISHABA', 'MITHUNA', 'KATAKA', 'SIMHA', 'KANYA',
    'THULA', 'VRICHIK', 'DHANUS', 'MAKARA', 'KUMBHA', 'MEENA'
];

const RASI_LORDS = ['KUJA', 'SUKR', 'BUDH', 'CHAN', 'SURY', 'BUDH', 'SUKR', 'KUJA', 'GURU', 'SANI', 'SANI', 'GURU'];

const PLANETS_SHORT = ['SURY', 'CHAN', 'KUJA', 'BUDH', 'GURU', 'SUKR', 'SANI', 'RAHU', 'KETU', 'LAGN', 'GULI', 'URAN', 'NEPT', 'PLUT'];

const WEEKDAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
const WEEKDAYS_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

// Nakshatra lords for Vimshottari Dasa (repeating for 27 nakshatras)
const NAKSHATRA_LORDS = ['KETU', 'SUKR', 'SURY', 'CHAN', 'KUJA', 'RAHU', 'GURU', 'SANI', 'BUDH'];
const DASA_YEARS = { KETU: 7, SUKR: 20, SURY: 6, CHAN: 10, KUJA: 7, RAHU: 18, GURU: 16, SANI: 19, BUDH: 17 };
const DASA_ORDER = ['SURY', 'CHAN', 'KUJA', 'RAHU', 'GURU', 'SANI', 'BUDH', 'KETU', 'SUKR'];
const TOTAL_DASA_YEARS = 120;

// Tamil year names (60-year Jovian cycle)
const TAMIL_YEARS = [
    'PRABHAVA', 'VIBHAVA', 'SUKLA', 'PRAMODOOTA', 'PRAJOTHPATTI', 'AANGIRASA', 'SRIMUKHA',
    'BHAVA', 'YUVA', 'DHAATU', 'ESWARA', 'BAHUDANYA', 'PRAMATHI', 'VIKRAMA', 'VISHU',
    'CHITRABAANU', 'SUBHAANU', 'TAARANA', 'PAARTHIBA', 'VYAYA', 'SARVAJIT', 'SARVADHARI',
    'VIRODHI', 'VIKRUTHI', 'KARA', 'NANDANA', 'VIJAYA', 'JAYA', 'MANMATHA', 'DUNMUKHI',
    'HEVILAMBI', 'VILAMBI', 'VIKRATHI', 'SARVARI', 'PLAVA', 'SUBAKRIT', 'SOBAKRIT',
    'KRODHI', 'VISWAVASU', 'PARABHAVA', 'PLAVANGA', 'KEELAKA', 'SOWMIYA', 'SADHARANA',
    'VIRODHIKRIT', 'PARIDHABI', 'PRAMADICHA', 'ANANDA', 'RAKSHASA', 'NALA', 'PINGALA',
    'KALAYUKTHI', 'SIDDHARTHI', 'RAUDRI', 'DUNMATHI', 'DUNDUBHI', 'RUDHRODGARI',
    'RAKTAKSHI', 'KRODHANA', 'AKSHAYA'
];

const TAMIL_MONTHS = [
    'CHITHIRAI', 'VAIKASI', 'AANI', 'AADI', 'AAVANI', 'PURATTASI',
    'AIPPASI', 'KARTHIGAI', 'MARGAZHI', 'THAI', 'MASI', 'PANGUNI'
];

// Thithi names
const THITHIS = [
    'PRATHAMA', 'DWITHIYA', 'THRITHIYA', 'CHATHURTH', 'PANCHAMI', 'SASHTI', 'SAPTHAMI',
    'ASHTAMI', 'NAVAMI', 'DASAMI', 'EKADASI', 'DWADASI', 'THRAYODAS', 'CHATHURDA', 'POURNAMI',
    'PRATHAMA', 'DWITHIYA', 'THRITHIYA', 'CHATHURTH', 'PANCHAMI', 'SASHTI', 'SAPTHAMI',
    'ASHTAMI', 'NAVAMI', 'DASAMI', 'EKADASI', 'DWADASI', 'THRAYODAS', 'CHATHURDA', 'AMAVASYA'
];

// Yoga names (27)
const YOGAS = [
    'VISHKAMB', 'PREETHI', 'AYUSHMAN', 'SOWBAGYA', 'SOBHANA', 'ATHIGAND', 'SUKARMA',
    'DHRITHI', 'SOOLA', 'GANDA', 'VRIDHI', 'DHRUVA', 'VYAGATHA', 'HARSHANA', 'VAJRA',
    'SIDDHI', 'VYATHIPA', 'VARIYAN', 'PARIGHA', 'SIVA', 'SIDDHA', 'SADHYA',
    'SUBHA', 'SUKLA', 'BRAHMA', 'INDRA', 'VAIDHRIT'
];

// Karana names (11 repeating)
const KARANAS = [
    'BAVA', 'BALAVA', 'KAULAVA', 'THYTHULA', 'GARAJA', 'VANIJA', 'VISHTI',
    'SAKUNI', 'CHATHUSHP', 'NAGA', 'KIMSTUGN'
];

// Planetary friendship table
const FRIENDSHIP = {
    SURY: { friends: ['CHAN', 'KUJA', 'GURU'], neutral: ['BUDH'], enemies: ['SUKR', 'SANI'] },
    CHAN: { friends: ['SURY', 'BUDH'], neutral: ['KUJA', 'GURU', 'SUKR', 'SANI'], enemies: [] },
    KUJA: { friends: ['SURY', 'CHAN', 'GURU'], neutral: ['SUKR', 'SANI'], enemies: ['BUDH'] },
    BUDH: { friends: ['SURY', 'SUKR'], neutral: ['KUJA', 'GURU', 'SANI'], enemies: ['CHAN'] },
    GURU: { friends: ['SURY', 'CHAN', 'KUJA'], neutral: ['SANI'], enemies: ['BUDH', 'SUKR'] },
    SUKR: { friends: ['BUDH', 'SANI'], neutral: ['KUJA', 'GURU'], enemies: ['SURY', 'CHAN'] },
    SANI: { friends: ['BUDH', 'SUKR'], neutral: ['GURU'], enemies: ['SURY', 'CHAN', 'KUJA'] }
};

// Nakshatra ruler mapping (each nakshatra's lord)
function getNakshatraLord(nakIdx) {
    return NAKSHATRA_LORDS[nakIdx % 9];
}

/**
 * Calculate day of week using Zeller's Congruence algorithm
 * Ported directly from SAA_ALL.BAS (lines 4730-4860).
 *
 * Uses the LOCAL birth date (day, month, year) as entered by the user,
 * NOT the UTC-converted Date object — avoids off-by-one errors for
 * early-morning births in timezones ahead of UTC (e.g. IST 0:00–5:30).
 *
 * Returns index 0–6 where 0=Sunday, 1=Monday, … 6=Saturday
 * (same convention as JS Date.getDay())
 */
function calcBirthWeekday(day, month, year) {
    // BASIC: ASTK = INT(.6 + (1 / ASTQ))  → 1 if Jan or Feb, else 0
    const ASTK = Math.floor(0.6 + (1 / month));
    // BASIC: ASTL = ASTY - ASTK  (adjusted year)
    const ASTL = year - ASTK;
    // BASIC: ASTO = ASTQ + 12 * ASTK  (adjusted month, Jan→13, Feb→14)
    const ASTO = month + 12 * ASTK;
    // BASIC: ASTP = ASTL / 100
    const ASTP = ASTL / 100;
    // BASIC: ASTZ1..ASTZ4
    const ASTZ1 = Math.floor(ASTP / 4);
    const ASTZ2 = Math.floor(ASTP);
    const ASTZ3 = Math.floor((5 * ASTL) / 4);
    const ASTZ4 = Math.floor(13 * (ASTO + 1) / 5);
    // BASIC: ASTZ = ASTZ4 + ASTZ3 - ASTZ2 + ASTZ1 + ASTE - 1
    let ASTZ = ASTZ4 + ASTZ3 - ASTZ2 + ASTZ1 + day - 1;
    // BASIC: ASTZ = ASTZ - (7 * INT(ASTZ / 7)) + 1  → 1=Sun..7=Sat
    ASTZ = ASTZ - (7 * Math.floor(ASTZ / 7)) + 1;
    // Convert from BASIC 1-based (1=Sun…7=Sat) to JS 0-based (0=Sun…6=Sat)
    return ASTZ - 1;
}

// ─────────────────────────── CORE CALCULATION ENGINE ────────────────────────

/**
 * Calculate Lahiri Ayanamsa for a given year (fractional)
 * Calibrated to match the original ASTRO Y2K software output.
 * Base at 1900 CE = 22°27'32.35" (Chitrapaksha/Lahiri standard)
 * Precession rate = 50.2564 arcseconds/year
 */
function calcAyanamsa(year) {
    return 22.458985 + (year - 1900) * 50.2564 / 3600;
}

/**
 * Get fractional year from a Date object
 */
function getFractionalYear(date) {
    const y = date.getUTCFullYear();
    const start = new Date(Date.UTC(y, 0, 1));
    const end = new Date(Date.UTC(y + 1, 0, 1));
    return y + (date.getTime() - start.getTime()) / (end.getTime() - start.getTime());
}

/**
 * Convert decimal degrees to DEG:MIN format string
 */
function degToStr(deg) {
    deg = ((deg % 360) + 360) % 360;
    const d = Math.floor(deg);
    const m = Math.round((deg - d) * 60);
    return `${String(d).padStart(3)}:${String(m).padStart(2, '0')}`;
}

/**
 * Get tropical longitude for a planet using astronomy-engine
 */
function getTropicalLongitude(body, astroTime) {
    if (body === 'Sun') {
        return Astronomy.SunPosition(astroTime as any).elon;
    }
    if (body === 'Moon') {
        return Astronomy.EclipticGeoMoon(astroTime).lon;
    }
    const vec = Astronomy.GeoVector(body, astroTime, true);
    const ecl = Astronomy.Ecliptic(vec);
    return ecl.elon;
}

/**
 * Calculate Mean Lunar Node (Rahu) - True node approximation
 */
function calcRahu(astroTime) {
    const JD = astroTime.tt + 2451545.0;
    const T = (JD - 2451545.0) / 36525.0;
    // Mean longitude of ascending node
    let omega = 125.04452 - 1934.136261 * T + 0.0020708 * T * T;
    return ((omega % 360) + 360) % 360;
}

/**
 * Calculate Ascendant (Lagna) from LST, latitude, and obliquity
 */
function calcAscendant(lstDeg, latDeg, obliquityDeg) {
    const lat = latDeg * Math.PI / 180;
    const obl = obliquityDeg * Math.PI / 180;
    const lst = lstDeg * Math.PI / 180;
    const y = Math.cos(lst);
    const x = -(Math.sin(lst) * Math.cos(obl) + Math.tan(lat) * Math.sin(obl));
    let asc = Math.atan2(y, x) * 180 / Math.PI;
    return ((asc % 360) + 360) % 360;
}

/**
 * Calculate Gulika (Mandi) longitude
 * Gulika is at Saturn's hora start from sunrise
 */
function calcGulika(weekday, sunriseFrac, sunsetFrac, lat, lon, ayanamsa, birthDate, timezone, year, month, day) {
    const dayStart = new Date(Date.UTC(year, month - 1, day, 0, 0, 0) - timezone * 3600000);
    const birthTimeFrac = (birthDate.getUTCHours() + birthDate.getUTCMinutes() / 60) / 24;

    let isNight = false;
    let actualWeekday = weekday;
    let startFrac = sunriseFrac;
    let lengthFrac = 0;

    if (birthTimeFrac < sunriseFrac) {
        // Pre-sunrise (belongs to previous day's night)
        isNight = true;
        actualWeekday = (weekday + 6) % 7;
        startFrac = sunsetFrac - 1; // Approx previous sunset
        lengthFrac = sunriseFrac - startFrac;
    } else if (birthTimeFrac > sunsetFrac) {
        // Post-sunset (belongs to today's night)
        isNight = true;
        startFrac = sunsetFrac;
        lengthFrac = (sunriseFrac + 1) - sunsetFrac; // Approx next sunrise
    } else {
        // Day birth
        startFrac = sunriseFrac;
        lengthFrac = sunsetFrac - sunriseFrac;
    }

    const gulikaHoraDay = [6, 5, 4, 3, 2, 1, 0];
    const gulikaHoraNight = [2, 1, 0, 6, 5, 4, 3];
    const horaLen = (lengthFrac * 24) / 8;
    const gIndex = isNight ? gulikaHoraNight[actualWeekday] : gulikaHoraDay[actualWeekday];
    const gulikaHrs = startFrac * 24 + (gIndex + 1) * horaLen; // Astro Y2K uses the end of the Saturn segment

    const gulikaDate = new Date(dayStart.getTime() + timezone * 3600000);
    gulikaDate.setUTCHours(0, 0, 0, 0);
    gulikaDate.setTime(gulikaDate.getTime() + gulikaHrs * 3600000 - timezone * 3600000);

    const gAt = Astronomy.MakeTime(gulikaDate);
    const gGmst = Astronomy.SiderealTime(gAt);
    const gLstDeg = ((gGmst * 15 + lon) % 360 + 360) % 360;
    const obliquity = 23.4393;
    const gulikaLagna = calcAscendant(gLstDeg, lat, obliquity);
    return ((gulikaLagna - ayanamsa) % 360 + 360) % 360;
}

/**
 * Get Nakshatra index and pada from sidereal longitude
 */
function getNakshatraPada(sidLon) {
    sidLon = ((sidLon % 360) + 360) % 360;
    const nakIdx = Math.floor(sidLon / (360 / 27));
    const posInNak = sidLon - nakIdx * (360 / 27);
    const pada = Math.floor(posInNak / (360 / 108)) + 1;
    return { index: nakIdx, pada: pada, name: NAKSHATRAS[nakIdx] };
}

/**
 * Get Rasi index from sidereal longitude
 */
function getRasi(sidLon) {
    sidLon = ((sidLon % 360) + 360) % 360;
    return Math.floor(sidLon / 30);
}

/**
 * Calculate Navamsa sign index from sidereal longitude
 */
function getNavamsa(sidLon) {
    sidLon = ((sidLon % 360) + 360) % 360;
    const padaTotal = Math.floor(sidLon / (360 / 108)); // 0-107
    return padaTotal % 12;
}

/**
 * Calculate Vargas (D2, D3, D10, D12, D30)
 */
function getVargas(sidLon) {
    sidLon = ((sidLon % 360) + 360) % 360;
    const sign = Math.floor(sidLon / 30);
    const degInSign = sidLon % 30;
    const isOdd = sign % 2 === 0; // 0=Aries (Odd)

    // D2 Hora: Parashara (Sun/Moon)
    let d2 = 0; // Aries=0... Leo=4, Cancer=3
    if (isOdd) d2 = (degInSign < 15) ? 4 : 3;
    else d2 = (degInSign < 15) ? 3 : 4;

    // D3 Drekkana
    const d3Part = Math.floor(degInSign / 10);
    const d3 = (sign + d3Part * 4) % 12;

    // D10 Dasamsa
    const d10Part = Math.floor(degInSign / 3);
    const d10 = isOdd ? (sign + d10Part) % 12 : (sign + 8 + d10Part) % 12;

    // D12 Dwadasamsa
    const d12Part = Math.floor(degInSign / 2.5);
    const d12 = (sign + d12Part) % 12;

    // D30 Trimsamsa
    let d30 = 0;
    if (isOdd) {
        if (degInSign < 5) d30 = 0; // Aries
        else if (degInSign < 10) d30 = 10; // Aquarius
        else if (degInSign < 18) d30 = 8; // Sagittarius
        else if (degInSign < 25) d30 = 2; // Gemini
        else d30 = 6; // Libra
    } else {
        if (degInSign < 5) d30 = 1; // Taurus
        else if (degInSign < 12) d30 = 5; // Virgo
        else if (degInSign < 20) d30 = 11; // Pisces
        else if (degInSign < 25) d30 = 9; // Capricorn
        else d30 = 7; // Scorpio
    }

    return { d2, d3, d10, d12, d30 };
}

/**
 * Calculate Sripati Bhava (house) cusps
 */
function calcBhavaCusps(lagnaLon, lstDeg, ayanamsa) {
    const obliquity = 23.4393;
    const obl = obliquity * Math.PI / 180;
    const lst = lstDeg * Math.PI / 180;

    const y = Math.sin(lst);
    const x = Math.cos(lst) * Math.cos(obl);
    let mcTrop = Math.atan2(y, x) * 180 / Math.PI;
    const mcSid = ((mcTrop - ayanamsa) % 360 + 360) % 360;

    const b1 = lagnaLon;
    const b10 = mcSid;
    const b4 = (b10 + 180) % 360;
    const b7 = (b1 + 180) % 360;

    const cusps = [];
    cusps[0] = b1;
    cusps[3] = b4;
    cusps[6] = b7;
    cusps[9] = b10;

    // First Quadrant: B1 to B4
    let arc1 = ((b4 - b1) % 360 + 360) % 360;
    cusps[1] = (b1 + arc1 / 3) % 360;
    cusps[2] = (b1 + 2 * arc1 / 3) % 360;

    // Second Quadrant: B4 to B7
    let arc2 = ((b7 - b4) % 360 + 360) % 360;
    cusps[4] = (b4 + arc2 / 3) % 360;
    cusps[5] = (b4 + 2 * arc2 / 3) % 360;

    // Third Quadrant: B7 to B10
    let arc3 = ((b10 - b7) % 360 + 360) % 360;
    cusps[7] = (b7 + arc3 / 3) % 360;
    cusps[8] = (b7 + 2 * arc3 / 3) % 360;

    // Fourth Quadrant: B10 to B1
    let arc4 = ((b1 - b10) % 360 + 360) % 360;
    cusps[10] = (b10 + arc4 / 3) % 360;
    cusps[11] = (b10 + 2 * arc4 / 3) % 360;

    const bhavas = [];
    for (let i = 0; i < 12; i++) {
        const mid = cusps[i];
        const prev = cusps[(i + 11) % 12]; // prev cusp
        let diff = ((mid - prev) % 360 + 360) % 360;
        const start = ((prev + diff / 2) % 360 + 360) % 360;
        bhavas.push({ bhava: i + 1, mid: mid, start: start, lord: RASI_LORDS[getRasi(mid)] });
    }
    return bhavas;
}

// ─────────────────────────── THITHI / YOGA / KARANA ─────────────────────────

function calcThithi(moonLon, sunLon) {
    let diff = ((moonLon - sunLon) % 360 + 360) % 360;
    const idx = Math.floor(diff / 12);
    const paksha = idx < 15 ? 'SUKLA' : 'KRISHNA';
    return { index: idx, name: THITHIS[idx], paksha: paksha };
}

function calcYoga(moonLon, sunLon) {
    let sum = ((moonLon + sunLon) % 360 + 360) % 360;
    const idx = Math.floor(sum / (360 / 27));
    return { index: idx, name: YOGAS[idx] };
}

function calcKarana(moonLon, sunLon) {
    let diff = ((moonLon - sunLon) % 360 + 360) % 360;
    const karanaIdx = Math.floor(diff / 6);
    let name;
    if (karanaIdx === 0) name = KARANAS[10]; // Kimstughna
    else if (karanaIdx >= 57) name = KARANAS[7 + (karanaIdx - 57)]; // Fixed karanas
    else name = KARANAS[(karanaIdx - 1) % 7];
    return { index: karanaIdx, name: name };
}

// ─────────────────────────── DASA / BUKTHI CALCULATOR ───────────────────────

function calcDasaBukthi(moonLon, birthDate) {
    const nak = getNakshatraPada(moonLon);
    const lordIdx = nak.index % 9;
    const lord = NAKSHATRA_LORDS[lordIdx];
    const dasaYears = DASA_YEARS[lord];

    // Elapsed fraction in the nakshatra
    const nakStart = nak.index * (360 / 27);
    const elapsed = moonLon - nakStart;
    const nakSpan = 360 / 27;
    const elapsedFrac = elapsed / nakSpan;
    const remainFrac = 1 - elapsedFrac;

    // Remaining years in birth dasa
    const remainYears = dasaYears * remainFrac;
    const remainDays = Math.round(remainYears * 365.25);

    // Build dasa sequence starting from birth lord
    const dasaSequence = [];
    let currentDate = new Date(birthDate.getTime());

    // First dasa (remaining)
    const endFirst = new Date(currentDate.getTime() + remainDays * 86400000);
    dasaSequence.push({
        lord: lord,
        startDate: new Date(currentDate),
        endDate: endFirst,
        years: remainYears,
        isFirst: true
    });
    currentDate = endFirst;

    // Subsequent dasas
    const lordOrder = [];
    let startIdx = NAKSHATRA_LORDS.indexOf(lord);
    for (let i = 1; i <= 8; i++) {
        const nextIdx = (startIdx + i) % 9;
        lordOrder.push(NAKSHATRA_LORDS[nextIdx]);
    }

    for (const dl of lordOrder) {
        const yrs = DASA_YEARS[dl];
        const days = Math.round(yrs * 365.25);
        const endDate = new Date(currentDate.getTime() + days * 86400000);
        dasaSequence.push({
            lord: dl,
            startDate: new Date(currentDate),
            endDate: endDate,
            years: yrs,
            isFirst: false
        });
        currentDate = endDate;
    }

    // Calculate bukthis for each dasa
    for (const dasa of dasaSequence) {
        dasa.bukthis = [];
        let bStart = new Date(dasa.startDate);
        let dIdx = NAKSHATRA_LORDS.indexOf(dasa.lord);
        for (let i = 0; i < 9; i++) {
            const bukLord = NAKSHATRA_LORDS[(dIdx + i) % 9];
            const bukYears = (dasa.years * DASA_YEARS[bukLord]) / TOTAL_DASA_YEARS;
            const bukDays = Math.round(bukYears * 365.25);
            const bEnd = new Date(bStart.getTime() + bukDays * 86400000);
            if (bEnd <= dasa.endDate || i === 8) {
                dasa.bukthis.push({
                    lord: bukLord,
                    startDate: new Date(bStart),
                    endDate: bEnd > dasa.endDate ? new Date(dasa.endDate) : bEnd
                });
            }
            bStart = bEnd;
        }
    }

    return {
        birthLord: lord,
        remainYears: remainYears,
        remainMonths: Math.floor((remainYears % 1) * 12),
        remainDays: Math.round(((remainYears % 1) * 12 % 1) * 30),
        sequence: dasaSequence
    };
}

// ─────────────────────────── ASHTAKAVARGA ───────────────────────────────────

/**
 * Generate Ashtakavarga tables using standard benefic-point rules
 */
function calcAshtakavarga(planetPositions) {
    const planets = ['SURY', 'CHAN', 'KUJA', 'BUDH', 'GURU', 'SUKR', 'SANI'];
    const rasis = 12;

    // Benefic positions from each planet (standard SAV rules)
    // Format: for each contributing planet, positions (houses from it) that give points
    const bavRules = {
        SURY: {
            SURY: [1, 2, 4, 7, 8, 9, 10, 11], CHAN: [3, 6, 10, 11], KUJA: [1, 2, 4, 7, 8, 9, 10, 11],
            BUDH: [3, 5, 6, 9, 10, 11, 12], GURU: [5, 6, 9, 11], SUKR: [6, 7, 12], SANI: [1, 2, 4, 7, 8, 9, 10, 11],
            LAGN: [3, 4, 6, 10, 11, 12]
        },
        CHAN: {
            SURY: [3, 6, 7, 8, 10, 11], CHAN: [1, 3, 6, 7, 10, 11], KUJA: [2, 3, 5, 6, 9, 10, 11],
            BUDH: [1, 3, 4, 5, 7, 8, 10, 11], GURU: [1, 4, 7, 8, 10, 11, 12], SUKR: [3, 4, 5, 7, 9, 10, 11],
            SANI: [3, 5, 6, 11], LAGN: [3, 6, 10, 11]
        },
        KUJA: {
            SURY: [3, 5, 6, 10, 11], CHAN: [3, 6, 11], KUJA: [1, 2, 4, 7, 8, 10, 11],
            BUDH: [3, 5, 6, 11], GURU: [6, 10, 11, 12], SUKR: [6, 8, 11, 12], SANI: [1, 4, 7, 8, 9, 10, 11],
            LAGN: [1, 3, 6, 10, 11]
        },
        BUDH: {
            SURY: [5, 6, 9, 11, 12], CHAN: [2, 4, 6, 8, 10, 11], KUJA: [1, 2, 4, 7, 8, 9, 10, 11],
            BUDH: [1, 3, 5, 6, 9, 10, 11, 12], GURU: [6, 8, 11, 12], SUKR: [1, 2, 3, 4, 5, 8, 9, 11],
            SANI: [1, 2, 4, 7, 8, 9, 10, 11], LAGN: [1, 2, 4, 6, 8, 10, 11]
        },
        GURU: {
            SURY: [1, 2, 3, 4, 7, 8, 9, 10, 11], CHAN: [2, 5, 7, 9, 11], KUJA: [1, 2, 4, 7, 8, 10, 11],
            BUDH: [1, 2, 4, 5, 6, 9, 10, 11], GURU: [1, 2, 3, 4, 7, 8, 10, 11], SUKR: [2, 5, 6, 9, 10, 11],
            SANI: [3, 5, 6, 12], LAGN: [1, 2, 4, 5, 6, 7, 9, 10, 11]
        },
        SUKR: {
            SURY: [8, 11, 12], CHAN: [1, 2, 3, 4, 5, 8, 9, 11, 12], KUJA: [3, 4, 5, 8, 9, 10, 11],
            BUDH: [3, 5, 6, 9, 11], GURU: [5, 8, 9, 10, 11], SUKR: [1, 2, 3, 4, 5, 8, 9, 10, 11],
            SANI: [3, 4, 5, 8, 9, 10, 11], LAGN: [1, 2, 3, 4, 5, 8, 9, 11]
        },
        SANI: {
            SURY: [1, 2, 4, 7, 8, 10, 11], CHAN: [3, 6, 11], KUJA: [3, 5, 6, 10, 11, 12],
            BUDH: [6, 8, 9, 10, 11, 12], GURU: [5, 6, 11, 12], SUKR: [6, 11, 12],
            SANI: [3, 5, 6, 11], LAGN: [1, 3, 4, 6, 10, 11]
        }
    };

    const result: Record<string, any> = {};
    const planets7 = [...planets];
    for (const planet of planets7) {
        result[planet] = new Array(12).fill(0);
        const rules = bavRules[planet];
        if (!rules) continue;
        for (const contributor of [...planets7, 'LAGN']) {
            const contribRasi = planetPositions[contributor];
            if (contribRasi === undefined || !rules[contributor]) continue;
            for (const houseNum of rules[contributor]) {
                const targetRasi = (contribRasi + houseNum - 1) % 12;
                result[planet][targetRasi]++;
            }
        }
    }

    // Sarvashtakavarga (raw totals)
    result.SARV = new Array(12).fill(0);
    for (const planet of planets7) {
        for (let i = 0; i < 12; i++) {
            result.SARV[i] += result[planet][i];
        }
    }

    // ── Trikonasodhana ──────────────────────────────────────────────────────
    // For each planet row: group signs into trines (1,5,9), (2,6,10), (3,7,11), (4,8,12).
    // Replace each group with the minimum of the three (if all equal → zero out).
    // Ported from SAA_ALL.BAS lines 9250-9330.
    const triRows: Record<string, any> = {};
    for (const planet of planets7) {
        triRows[planet] = [...result[planet]]; // copy
    }
    // Also do the sarvashtakavarga total row
    triRows.SARV = [...result.SARV];

    for (const key of [...planets7, 'SARV']) {
        const row = triRows[key];
        for (let b = 0; b < 4; b++) { // b=0..3 → signs 0,4,8 ; 1,5,9 ; 2,6,10 ; 3,7,11
            const i0 = b, i1 = b + 4, i2 = b + 8;
            if (row[i0] === row[i1] && row[i1] === row[i2]) {
                row[i0] = row[i1] = row[i2] = 0;
            } else {
                const least = Math.min(row[i0], row[i1], row[i2]);
                row[i0] = least; row[i1] = least; row[i2] = least;
            }
        }
    }

    return { raw: result, trikonasodhana: triRows };
}



// ─────────────────────────── HORA & TRIMSAMSA LORDS ────────────────────────

/**
 * Hora Lord (half-sign ruler) for a sidereal longitude.
 * Ported from SAA_ALL.BAS lines 8370-8400.
 * Even half → Moon (index 3); Odd half → Sun (index 2).
 */
function calcHoraLord(lon) {
    const x = Math.floor((lon - 15) / 30);
    return (x % 2 === 0) ? 'CHAN' : 'SURY';
}

/**
 * Trimsamsa Lord — traditional sub-division ruler.
 * Ported from SAA_ALL.BAS lines 8420-8570.
 * rasiIdx: 0-based (0=Mesha…11=Meena). Odd-numbered rasi (0,2,4…) = Mesha-group rules.
 */
function calcTrimsamsaLord(lon, rasiIdx) {
    const T = lon % 30;
    const isOdd = rasiIdx % 2 === 0; // Mesha=0 is odd sign (1st)
    if (isOdd) {
        if (T <= 5)  return 'KUJA';
        if (T <= 10) return 'SANI';
        if (T <= 18) return 'GURU';
        if (T <= 25) return 'BUDH';
        return 'SUKR';
    } else {
        if (T <= 5)  return 'SUKR';
        if (T <= 10) return 'BUDH';
        if (T <= 18) return 'GURU';
        if (T <= 25) return 'SANI';
        return 'KUJA';
    }
}

/**
 * Sapthamsa (D7) chart rasi index.
 * Ported from SAA_ALL.BAS lines 6670-6710.
 */
function getSapthamsa(lon) {
    lon = ((lon % 360) + 360) % 360;
    const T = Math.floor(lon * 7 / 30) + 1;
    const idx = T % 12;
    return idx === 0 ? 12 - 1 : idx - 1; // 0-based
}

// ─────────────────────────── CHART RENDERING ────────────────────────────────

/**
 * Render South Indian style chart (text-based)
 */
function renderSouthIndianChart(planetsByRasi, title) {
    // South Indian chart layout (fixed rasi positions):
    //  MEENA  | MESHA  | RISHA  | MITHU
    //  KUMBHA |        center   | KATAKA
    //  MAKARA |                 | SIMHA
    //  DHANUS | VRICH  | THULA  | KANYA
    const layout = [
        [11, 0, 1, 2],   // row 0
        [10, -1, -1, 3],   // row 1 (center)
        [9, -1, -1, 4],   // row 2 (center)
        [8, 7, 6, 5]    // row 3
    ];

    const cellWidth = 9;
    const lines = [];

    // Top border
    lines.push('+' + ('-'.repeat(cellWidth - 1) + '+').repeat(4));

    for (let row = 0; row < 4; row++) {
        // Two lines per cell: planets line 1 and line 2
        const line1Parts = [];
        const line2Parts = [];
        for (let col = 0; col < 4; col++) {
            const rasiIdx = layout[row][col];
            if (rasiIdx === -1) {
                if (row === 1 && col === 1) {
                    line1Parts.push((' '.repeat(cellWidth * 2 - 1)));
                    line2Parts.push((' '.repeat(cellWidth * 2 - 1)));
                    col++; // skip next col
                    continue;
                }
            }
            const planets = planetsByRasi[rasiIdx] || [];
            const pl = planets.map(p => p.substring(0, 3));
            let s1 = pl.slice(0, 2).join(' ');
            let s2 = pl.slice(2, 4).join(' ');
            if (pl.length > 4) {
                s1 = pl.slice(0, 2).join(' ') + pl.length; // Fallback to indicate hidden planets, keeping max 8 char width
            }
            line1Parts.push(s1.padEnd(cellWidth - 1).substring(0, cellWidth - 1));
            line2Parts.push(s2.padEnd(cellWidth - 1).substring(0, cellWidth - 1));
        }
        lines.push('|' + line1Parts.join('|') + '|');
        lines.push('|' + line2Parts.join('|') + '|');

        if (row === 1) {
            const titlePad = title.padStart(Math.floor((cellWidth * 2 - 1 + title.length) / 2)).padEnd(cellWidth * 2 - 1);
            lines.push('+' + ('-'.repeat(cellWidth - 1)) + '+' + titlePad + '+' + ('-'.repeat(cellWidth - 1)) + '+');
        } else {
            lines.push('+' + ('-'.repeat(cellWidth - 1) + '+').repeat(4));
        }
    }

    return lines;
}

// Dynamic Rasi chart rendering for web/mobile
// ─────────────────────────── MAIN HOROSCOPE GENERATOR ───────────────────────

function generateHoroscope(input, lang: HoroscopeLang = 'en') {
    const { name, day, month, year, hour, minute, timezone, place, latDeg, latMin, latDir, lonDeg, lonMin, lonDir } = input;
    const L = getLangDict(lang);

    // Build date
    const totalHours = hour + minute / 60;
    const tzOffset = timezone >= 0 ? `-${String(Math.floor(Math.abs(timezone))).padStart(2, '0')}:${String(Math.round((Math.abs(timezone) % 1) * 60)).padStart(2, '0')}`
        : `+${String(Math.floor(Math.abs(timezone))).padStart(2, '0')}:${String(Math.round((Math.abs(timezone) % 1) * 60)).padStart(2, '0')}`;

    const lat = (latDeg + latMin / 60) * (latDir === 'S' ? -1 : 1);
    const lon = (lonDeg + lonMin / 60) * (lonDir === 'W' ? -1 : 1);

    // Construct UTC date
    const localMs = Date.UTC(year, month - 1, day, hour, minute, 0);
    const utcMs = localMs - timezone * 3600000;
    const birthDate = new Date(utcMs);
    const astroTime = Astronomy.MakeTime(birthDate);

    // Fractional year for ayanamsa
    const fracYear = getFractionalYear(birthDate);
    const ayanamsa = calcAyanamsa(fracYear);

    // Observer for sunrise/sunset
    const observer = new Astronomy.Observer(lat, lon, 0);

    // Sunrise/Sunset
    const dayStart = new Date(Date.UTC(year, month - 1, day, 0, 0, 0) - timezone * 3600000);
    const dayStartAstro = Astronomy.MakeTime(dayStart);
    const sunriseResult = Astronomy.SearchRiseSet(('Sun' as any), observer, +1, dayStartAstro, 1);
    const sunsetResult = Astronomy.SearchRiseSet(('Sun' as any), observer, -1, dayStartAstro, 1);

    let sunriseStr = 'N/A', sunsetStr = 'N/A';
    let sunriseFrac = 0.25, sunsetFrac = 0.75; // defaults
    if (sunriseResult) {
        const srLocal = new Date(sunriseResult.date.getTime() + timezone * 3600000);
        sunriseStr = `${srLocal.getUTCHours()}:${String(srLocal.getUTCMinutes()).padStart(2, '0')}`;
        sunriseFrac = (srLocal.getUTCHours() + srLocal.getUTCMinutes() / 60) / 24;
    }
    if (sunsetResult) {
        const ssLocal = new Date(sunsetResult.date.getTime() + timezone * 3600000);
        sunsetStr = `${ssLocal.getUTCHours()}:${String(ssLocal.getUTCMinutes()).padStart(2, '0')}`;
        sunsetFrac = (ssLocal.getUTCHours() + ssLocal.getUTCMinutes() / 60) / 24;
    }

    // Sidereal time at birth
    const gmst = Astronomy.SiderealTime(astroTime);
    const lstHours = ((gmst + lon / 15) % 24 + 24) % 24;
    const lstDeg = lstHours * 15;
    const stH = Math.floor(lstHours);
    const stM = Math.floor((lstHours - stH) * 60);
    const stS = Math.round(((lstHours - stH) * 60 - stM) * 60);

    // Obliquity
    const obliquity = 23.4393;

    // ── PLANETARY LONGITUDES ──
    const tropicalPositions: Record<string, any> = {};
    tropicalPositions.SURY = getTropicalLongitude('Sun', astroTime);
    tropicalPositions.CHAN = getTropicalLongitude('Moon', astroTime);
    tropicalPositions.KUJA = getTropicalLongitude('Mars', astroTime);
    tropicalPositions.BUDH = getTropicalLongitude('Mercury', astroTime);
    tropicalPositions.GURU = getTropicalLongitude('Jupiter', astroTime);
    tropicalPositions.SUKR = getTropicalLongitude('Venus', astroTime);
    tropicalPositions.SANI = getTropicalLongitude('Saturn', astroTime);
    tropicalPositions.URAN = getTropicalLongitude('Uranus', astroTime);
    tropicalPositions.NEPT = getTropicalLongitude('Neptune', astroTime);
    tropicalPositions.PLUT = getTropicalLongitude('Pluto', astroTime);
    tropicalPositions.RAHU = calcRahu(astroTime);

    // Sidereal positions
    const siderealPositions: Record<string, any> = {};
    for (const [key, val] of Object.entries(tropicalPositions)) {
        siderealPositions[key] = ((val - ayanamsa) % 360 + 360) % 360;
    }
    siderealPositions.KETU = (siderealPositions.RAHU + 180) % 360;

    // Ascendant
    const lagnaT = calcAscendant(lstDeg, lat, obliquity);
    siderealPositions.LAGN = ((lagnaT - ayanamsa) % 360 + 360) % 360;

    // Gulika
    const weekday = birthDate.getUTCDay();
    siderealPositions.GULI = calcGulika(weekday, sunriseFrac, sunsetFrac, lat, lon, ayanamsa, birthDate, timezone, year, month, day);

    // ── NAKSHATRA & RASI FOR EACH PLANET ──
    const planetData: Record<string, any> = {};
    const rasiPlanets: Record<string, any> = {}; // rasi index → planet names
    const navamsaPlanets: Record<string, any> = {};
    for (let i = 0; i < 12; i++) { rasiPlanets[i] = []; navamsaPlanets[i] = []; }

    const allPlanets = ['SURY', 'CHAN', 'KUJA', 'BUDH', 'GURU', 'SUKR', 'SANI', 'RAHU', 'KETU', 'LAGN', 'GULI', 'URAN', 'NEPT', 'PLUT'];

    for (const p of allPlanets) {
        const lon = siderealPositions[p];
        const nak = getNakshatraPada(lon);
        const rasiIdx = getRasi(lon);
        const navIdx = getNavamsa(lon);
        planetData[p] = {
            longitude: lon,
            degStr: degToStr(lon),
            nakshatra: nak.name,
            nakIndex: nak.index,
            pada: nak.pada,
            rasi: rasiIdx,
            rasiName: RASIS[rasiIdx],
            navamsa: navIdx,
            navamsaName: RASIS[navIdx],
            ruler: RASI_LORDS[rasiIdx]
        };
        rasiPlanets[rasiIdx].push(p);
        navamsaPlanets[navIdx].push(p);
    }

    // ── PANCHANGA ELEMENTS ──
    const thithi = calcThithi(siderealPositions.CHAN, siderealPositions.SURY);
    const yoga = calcYoga(siderealPositions.CHAN, siderealPositions.SURY);
    const karana = calcKarana(siderealPositions.CHAN, siderealPositions.SURY);
    const birthNak = getNakshatraPada(siderealPositions.CHAN);

    // Tamil year
    let tamilYearIdx = (year % 60) - 6;
    if (tamilYearIdx <= 0) tamilYearIdx += 60;
    const tamilYear = TAMIL_YEARS[tamilYearIdx - 1] || 'UNKNOWN';

    // Approximate Tamil month (from sun's sidereal position)
    const sunSidLon = siderealPositions.SURY;
    const tamilMonthIdx = Math.floor(sunSidLon / 30);
    const tamilMonth = TAMIL_MONTHS[tamilMonthIdx] || 'CHITHIRAI';
    const tamilDay = Math.floor((sunSidLon % 30) + 1);

    // Day/Night birth
    const birthHourFrac = (hour + minute / 60) / 24;
    const isDayBirth = birthHourFrac >= sunriseFrac && birthHourFrac <= sunsetFrac;

    // ── DASA / BUKTHI ──
    const dasaInfo = calcDasaBukthi(siderealPositions.CHAN, birthDate);

    // Find current running dasa/bukthi (as of today)
    const today = new Date();
    let currentDasa = '', currentBukthi = '', currentBukthiEnd: any = null;
    for (const dasa of dasaInfo.sequence) {
        if (today >= dasa.startDate && today <= dasa.endDate) {
            currentDasa = dasa.lord;
            for (const buk of dasa.bukthis) {
                if (today >= buk.startDate && today <= buk.endDate) {
                    currentBukthi = buk.lord;
                    currentBukthiEnd = buk.endDate;
                    break;
                }
            }
            break;
        }
    }

    // ── RETROGRADE CHECK ──
    const retroPlanets = [];
    const retroBodies = { BUDH: 'Mercury', SUKR: 'Venus', KUJA: 'Mars', GURU: 'Jupiter', SANI: 'Saturn', URAN: 'Uranus', NEPT: 'Neptune', PLUT: 'Pluto' };
    for (const [abbr, body] of Object.entries(retroBodies)) {
        try {
            const vec1 = Astronomy.GeoVector((body as any), astroTime, true);
            const t2 = Astronomy.MakeTime(new Date(birthDate.getTime() + 86400000));
            const vec2 = Astronomy.GeoVector((body as any), t2, true);
            const ecl1 = Astronomy.Ecliptic(vec1);
            const ecl2 = Astronomy.Ecliptic(vec2);
            let diff = ecl2.elon - ecl1.elon;
            if (diff > 180) diff -= 360;
            if (diff < -180) diff += 360;
            if (diff < 0) retroPlanets.push(abbr);
        } catch (e) { }
    }

    // ── BHAVA CUSPS ──
    const bhavas = calcBhavaCusps(siderealPositions.LAGN, lstDeg, ayanamsa);
    const bhavaPlanets: Record<string, any> = {};
    for (let i = 0; i < 12; i++) bhavaPlanets[i] = [];
    for (const p of allPlanets) {
        const pLon = siderealPositions[p];
        // Find which bhava the planet falls in
        for (let i = 0; i < 12; i++) {
            const start = bhavas[i].start;
            const end = (start + 30) % 360;
            let inBhava;
            if (end > start) inBhava = pLon >= start && pLon < end;
            else inBhava = pLon >= start || pLon < end;
            if (inBhava) { bhavaPlanets[i].push(p); break; }
        }
    }

    // Add degStr to bhavas for PDF template
    for (let i = 0; i < 12; i++) {
        bhavas[i].midDeg = degToStr(bhavas[i].mid);
        bhavas[i].startDeg = degToStr(bhavas[i].start);
    }

    // ── ASHTAKAVARGA ──
    const planetRasis: Record<string, any> = {};
    for (const p of ['SURY', 'CHAN', 'KUJA', 'BUDH', 'GURU', 'SUKR', 'SANI', 'LAGN']) {
        planetRasis[p] = getRasi(siderealPositions[p]);
    }
    const ashtaka = calcAshtakavarga(planetRasis);

    // ─────────────────── FORMAT OUTPUT ───────────────────
    const output = [];
    const todayStr = `${today.getDate()}-${String(today.getMonth() + 1).padStart(2, ' ')}-${today.getFullYear()}`;
    // Use Zeller's Congruence (ported from SAA_ALL.BAS lines 4730-4860) to get
    // the correct weekday from the LOCAL birth date (day/month/year as entered).
    // Do NOT use birthDate.getDay() — that depends on machine timezone and the
    // UTC-converted time, causing wrong results for early-morning IST births.
    const birthWeekday = calcBirthWeekday(day, month, year);

    // Page 1 Header
    const lTamilYear = L.TAMIL_YEARS[tamilYearIdx - 1] || tamilYear;
    const lTamilMonth = L.TAMIL_MONTHS[tamilMonthIdx] || tamilMonth;
    const lBirthNak = L.NAKSHATRAS[birthNak.index] || birthNak.name;
    const lThithi = L.THITHIS[thithi.index] || thithi.name;
    const lPaksha = L.PAKSHA[thithi.paksha] || thithi.paksha;
    const lYoga = L.YOGAS[yoga.index] || yoga.name;
    const lKarana = L.KARANAS[karana.index >= 57 ? 7 + (karana.index - 57) : karana.index === 0 ? 10 : (karana.index - 1) % 7] || karana.name;
    const lWeekday = L.WEEKDAYS[birthWeekday];
    output.push(` ${L.HOROSCOPE_OF} ${name.padEnd(35)} (VAKYA JS/01 )${todayStr} ${L.PAGE}:1`);
    output.push(` ${L.BORN}:${String(day).padStart(2)}-${String(month).padStart(2, ' ')}-${year} ${lWeekday.padEnd(10)} ${String(hour).padStart(2)}:${String(minute).padStart(2, '0')}(TIME ZONE ${timezone}) ${place.padEnd(16)} ${String(latDeg).padStart(2)}:${String(latMin).padStart(2, '0')}${latDir} ${String(lonDeg).padStart(2)}:${String(lonMin).padStart(2, '0')}${lonDir}`);
    output.push(` ${L.SUNRISE_SET} ${sunriseStr}/${sunsetStr} IST     ${L.SIDEREAL_TIME} ${String(stH).padStart(2)}:${String(stM).padStart(2, '0')}:${String(stS).padStart(2, '0')}     ${L.AYANAMSA} ${Math.floor(ayanamsa)}:${String(Math.floor((ayanamsa % 1) * 60)).padStart(2, '0')}:${String(Math.round(((ayanamsa % 1) * 60 % 1) * 60)).padStart(2, '0')}`);
    output.push(` ${lTamilYear.padEnd(9)} ${lTamilMonth.padEnd(9)} ${String(tamilDay).padStart(2)}(${isDayBirth ? L.DAY + '  ' : L.NIGHT}) ${lBirthNak} ${L.STAR},${L.PADA} ${birthNak.pada}, ${lPaksha}  ${lThithi.padEnd(9)} ${L.THITHI_LABEL},`);
    output.push(`               ${lYoga} ${L.YOGA_LABEL}, ${lKarana} ${L.KARANA_LABEL}`);
    output.push('');

    // Nirayana Longitudes
    output.push(`                             ${L.NIRAYANA}`);
    output.push(` ${L.PLANET} DEG:MN  ${L.STAR}   ${L.PADA}   ${L.RULER}        ${L.PLANET} DEG:MN  ${L.STAR}   ${L.PADA}   ${L.RULER}`);

    const leftPlanets = ['SURY', 'KUJA', 'GURU', 'SANI', 'KETU', 'GULI', 'NEPT'];
    const rightPlanets = ['CHAN', 'BUDH', 'SUKR', 'RAHU', 'LAGN', 'URAN', 'PLUT'];

    for (let i = 0; i < leftPlanets.length; i++) {
        const lp = leftPlanets[i];
        const rp = rightPlanets[i];
        const ld = planetData[lp];
        const rd = planetData[rp];
        const lNak = L.NAKSHATRAS[getNakshatraPada(siderealPositions[lp]).index] || ld.nakshatra;
        const rNak = L.NAKSHATRAS[getNakshatraPada(siderealPositions[rp]).index] || rd.nakshatra;
        const lPl = (L.PLANETS_FULL[lp] || lp).padEnd(4);
        const rPl = (L.PLANETS_FULL[rp] || rp).padEnd(4);
        const lLine = ` ${lPl} ${ld.degStr}  ${lNak.padEnd(9)} ${ld.pada}    ${(L.PLANETS_FULL[ld.ruler] || ld.ruler).padEnd(4)}`;
        const rLine = `    ${rPl} ${rd.degStr}  ${rNak.padEnd(9)} ${rd.pada}    ${L.PLANETS_FULL[rd.ruler] || rd.ruler}`;
        output.push(lLine + rLine);
    }
    output.push('');

    // Retrograde planets
    if (retroPlanets.length > 0) {
        const retroNames = retroPlanets.map(p => L.PLANETS_FULL[p] || p);
        output.push(` ${L.RETROGRESSION} : ${retroNames.join(';')};`);
    }

    // Rasi Chart — use localized planet abbreviations in charts
    const localRasiPlanets: Record<string, any> = {};
    const localNavamsaPlanets: Record<string, any> = {};
    for (let ri = 0; ri < 12; ri++) {
        localRasiPlanets[ri] = (rasiPlanets[ri] || []).map(p => L.PLANETS[p] || p.substring(0, 3));
        localNavamsaPlanets[ri] = (navamsaPlanets[ri] || []).map(p => L.PLANETS[p] || p.substring(0, 3));
    }
    const rasiChart = renderSouthIndianChart(localRasiPlanets, L.RASI_LABEL);
    const navamsaChart = renderSouthIndianChart(localNavamsaPlanets, L.NAVAMSA_LABEL);

    // Print charts side by side
    for (let i = 0; i < rasiChart.length; i++) {
        const rLine = rasiChart[i] || '';
        const nLine = navamsaChart[i] || '';
        output.push(' ' + rLine.padEnd(39) + ' ' + nLine);
    }
    output.push('');

    // Dasa/Bukthi
    const remYrs = Math.floor(dasaInfo.remainYears);
    const remMon = dasaInfo.remainMonths;
    const remDay = dasaInfo.remainDays;
    const lBirthLord = L.PLANETS_FULL[dasaInfo.birthLord] || dasaInfo.birthLord;
    output.push(`       ${lBirthLord} ${L.DASA_REMAINING}  ${remYrs} ${L.YEARS} ${remMon} ${L.MONTHS_LABEL} ${remDay} ${L.DAYS_LABEL}`);
    if (currentDasa) {
        const bukEndStr = currentBukthiEnd ? `${currentBukthiEnd.getDate()}-${String(currentBukthiEnd.getMonth() + 1).padStart(2, ' ')}-${currentBukthiEnd.getFullYear()}` : '';
        const lCurDasa = L.PLANETS_FULL[currentDasa] || currentDasa;
        const lCurBuk = L.PLANETS_FULL[currentBukthi] || currentBukthi;
        output.push(`       ON ${todayStr}  ${lCurDasa} ${L.DASA} ${lCurBuk} ${L.BUKTHI} ${L.RUNNING}(${L.TILL} ${bukEndStr})`);
    }
    output.push('');

    // Dasa period listing
    output.push(' DASA ENDS        BUKTHI ENDS BUKTHI ENDS BUKTHI ENDS BUKTHI ENDS BUKTHI ENDS ');
    output.push(' ' + '-'.repeat(76));
    for (const dasa of dasaInfo.sequence) {
        const endStr = `${String(dasa.endDate.getDate()).padStart(2)}-${String(dasa.endDate.getMonth() + 1).padStart(2, ' ')}-${dasa.endDate.getFullYear()}`;
        let line1 = ` ${dasa.lord.padEnd(4)} ${endStr}`;
        let line2 = '                  ';
        const bukParts = [];
        for (const buk of dasa.bukthis) {
            const bd = buk.endDate;
            const months3 = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
            const bukStr = `${buk.lord.substring(0, 3)} ${String(bd.getDate()).padStart(2)}${months3[bd.getMonth()]}${String(bd.getFullYear()).slice(-2)}`;
            bukParts.push(bukStr);
        }
        // First 4 bukthis on line 1
        line1 += '  ' + bukParts.slice(0, 4).join(' ');
        line2 += bukParts.slice(4).join(' ');
        output.push(line1);
        if (bukParts.length > 4) output.push(line2);
    }
    output.push('');

    // Bhava Chart
    output.push('');
    output.push(' BAVA MIDDL  START   BAVA  BAVA MIDDL  START   BAVA  BAVA MIDDL  START   BAVA ');
    output.push('     DEG:MN  DEG:MN  LORD      DEG:MN  DEG:MN  LORD      DEG:MN  DEG:MN  LORD ');
    output.push('');

    for (let row = 0; row < 4; row++) {
        let line = '';
        for (let col = 0; col < 3; col++) {
            const idx = row * 3 + col;
            const b = bhavas[idx];
            line += `  ${String(idx + 1).padStart(2)}  ${degToStr(b.mid)}  ${degToStr(b.start)}  ${b.lord.padEnd(4)}`;
        }
        output.push(line);
    }

    // Page 2 header
    output.push('\f');
    output.push(` ${L.HOROSCOPE_OF} ${name.padEnd(35)} (VAKYA JS/01 )${todayStr} ${L.PAGE}:2`);

    // Bhava Chart visual — localized planet names
    const localBhavaPlanets: Record<string, any> = {};
    for (let bi = 0; bi < 12; bi++) {
        localBhavaPlanets[bi] = (bhavaPlanets[bi] || []).map(p => L.PLANETS[p] || p.substring(0, 3));
    }
    const bhavaChart = renderSouthIndianChart(localBhavaPlanets, L.BHAVA_LABEL);
    for (const line of bhavaChart) {
        output.push('                     ' + line);
    }
    output.push('');

    // ── ASHTAKAVARGA (RAW) with correct Pindas ──────────────────────────────
    // RasPinda = sum of all 12 signs' scores
    // GrhaPinda = planet's own-sign score x 3 (standard ASTRO Y2K formula)
    // SodPinda  = RasPinda + GrhaPinda
    output.push(`                     ${L.ASHTAKAVARGA}                          ${L.PINDAS}`);
    let avHeader = '       ';
    for (const r of RASIS) avHeader += r.substring(0, 4).padStart(5);
    avHeader += '  RAS  GRH  SOD';
    output.push(avHeader);

    const avPlanets = ['SURY', 'CHAN', 'KUJA', 'BUDH', 'GURU', 'SUKR', 'SANI'];
    for (const p of avPlanets) {
        let line = ` ${p.padEnd(4)} `;
        let rasPinda = 0;
        const pRasi = planetRasis[p];
        for (let i = 0; i < 12; i++) {
            const val = ashtaka.raw[p][i];
            const marker = (pRasi === i) ? '*' : ' ';
            line += `${marker}${String(val).padStart(2)}/0`;
            rasPinda += val;
        }
        const grhPinda = Math.round(rasPinda * 0.5);
        const sodPinda = rasPinda + grhPinda;
        line += `  ${String(rasPinda).padStart(3)}  ${String(grhPinda).padStart(3)}  ${String(sodPinda).padStart(3)}`;
        output.push(line);
    }
    output.push('');

    // Raw Sarvashtakavarga totals
    let sarvLine = ' SARV ';
    for (let i = 0; i < 12; i++) {
        sarvLine += String(ashtaka.raw.SARV[i]).padStart(3) + '  ';
    }
    output.push(sarvLine);
    output.push('');

    // ── TRIKONASODHANA (purified AV) ───────────────────────────────────────
    // SAA_ALL.BAS lines 9250-9420: replace each trine-group with the minimum
    output.push(`                     ${L.TRIKONASODHANA}`);
    let triHeader = '       ';
    for (const r of RASIS) triHeader += r.substring(0, 4).padStart(5);
    triHeader += '  TOT';
    output.push(triHeader);
    for (const p of avPlanets) {
        let tLine = ` ${p.padEnd(4)} `;
        let sodTotal = 0;
        const pRasi = planetRasis[p];
        for (let i = 0; i < 12; i++) {
            const val = ashtaka.trikonasodhana[p][i];
            const marker = (pRasi === i) ? '*' : ' ';
            tLine += `${marker}${String(val).padStart(2)}/0`;
            sodTotal += val;
        }
        tLine += `  ${String(sodTotal).padStart(3)}`;
        output.push(tLine);
    }
    let sodSarvLine = ' TOTAL';
    for (let i = 0; i < 12; i++) sodSarvLine += String(ashtaka.trikonasodhana.SARV[i]).padStart(5);
    output.push(sodSarvLine);
    output.push('  (TOTAL = Sodhana for Sarvashtakavargam)');
    
    // Page 2 ends here
    for (let i = 0; i < 10; i++) output.push('');
    output.push('\f');
    output.push(` ${L.HOROSCOPE_OF} ${name.padEnd(35)} (VAKYA JS/01 )${todayStr} ${L.PAGE}:3`);
    output.push('');

    // Vargas Charts Arrays - includes D7 (Sapthamsa)
    const vargas = { D2: {}, D3: {}, D7: {}, D10: {}, D12: {}, D30: {} };
    for (const p of allPlanets) {
        const pVargas = getVargas(siderealPositions[p]);
        const d7 = getSapthamsa(siderealPositions[p]);
        if (!vargas.D2[pVargas.d2]) vargas.D2[pVargas.d2] = [];
        if (!vargas.D3[pVargas.d3]) vargas.D3[pVargas.d3] = [];
        if (!vargas.D7[d7])         vargas.D7[d7] = [];
        if (!vargas.D10[pVargas.d10]) vargas.D10[pVargas.d10] = [];
        if (!vargas.D12[pVargas.d12]) vargas.D12[pVargas.d12] = [];
        if (!vargas.D30[pVargas.d30]) vargas.D30[pVargas.d30] = [];
        vargas.D2[pVargas.d2].push(p);
        vargas.D3[pVargas.d3].push(p);
        vargas.D7[d7].push(p);
        vargas.D10[pVargas.d10].push(p);
        vargas.D12[pVargas.d12].push(p);
        vargas.D30[pVargas.d30].push(p);
    }

    // Drekkana (D3) and Dwadasamsa (D12) side by side
    const d3Chart = renderSouthIndianChart(vargas.D3, 'DREKKANA');
    const d12Chart = renderSouthIndianChart(vargas.D12, 'DWADASAMSA');
    for (let i = 0; i < d3Chart.length; i++) {
        output.push(' ' + (d3Chart[i] || '').padEnd(39) + ' ' + (d12Chart[i] || ''));
    }
    output.push('');

    // Sapthamsa (D7) and Navamsa side by side
    const d7Chart = renderSouthIndianChart(vargas.D7, 'SAPTHAMSA');
    const navChartV2 = renderSouthIndianChart(navamsaPlanets, 'NAVAMSA');
    for (let i = 0; i < d7Chart.length; i++) {
        output.push(' ' + (d7Chart[i] || '').padEnd(39) + ' ' + (navChartV2[i] || ''));
    }
    output.push('');

    // ── HORA LORD & TRIMSAMSA LORD TABLE ──────────────────────────────────
    // SAA_ALL.BAS lines 8590-8660
    output.push(' PLANET          HORA LORD       TRIMSAMSA LORD');
    output.push(' ' + '-'.repeat(47));
    for (const p of ['LAGN','SURY','CHAN','KUJA','BUDH','GURU','SUKR','SANI','RAHU','KETU']) {
        const lon = siderealPositions[p];
        const rasiIdx = Math.floor(((lon % 360) + 360) % 360 / 30);
        const hora  = calcHoraLord(lon);
        const trims = calcTrimsamsaLord(lon, rasiIdx);
        output.push(` ${p.padEnd(16)} ${hora.padEnd(16)} ${trims}`);
    }
    output.push('');

    const vargasTextTable: Record<string, any> = {};
    for (const p of ['SURY','CHAN','KUJA','BUDH','GURU','SUKR','SANI','RAHU','KETU','LAGN']) {
        const lon = siderealPositions[p];
        const rasiIdx = Math.floor(((lon % 360) + 360) % 360 / 30);
        const hora  = calcHoraLord(lon);
        const trims = calcTrimsamsaLord(lon, rasiIdx);
        const v = getVargas(lon);
        const sapt = getSapthamsa(lon);
        const nav = getNavamsa(lon);
        vargasTextTable[p] = {
            rasi: rasiIdx,
            hora: hora,
            drekkana: v.d3,
            sapthamsa: sapt,
            navamsa: nav,
            dwadasamsa: v.d12,
            trimsamsa: trims,
            dasamsa: v.d10
        };
    }

    // ── EXALTATION & DEBILITATION ──────────────────────────────────────────
    const UCHCHA_R = [
        { p:'SURY', min:0,   max:30  }, { p:'CHAN', min:30,  max:60  },
        { p:'KUJA', min:270, max:300 }, { p:'BUDH', min:150, max:180 },
        { p:'GURU', min:90,  max:120 }, { p:'SUKR', min:330, max:360 },
        { p:'SANI', min:180, max:210 }, { p:'RAHU', min:210, max:240 },
        { p:'KETU', min:300, max:330 }
    ];
    const NEECHA_R = [
        { p:'SURY', min:180, max:210 }, { p:'CHAN', min:210, max:240 },
        { p:'KUJA', min:90,  max:120 }, { p:'BUDH', min:330, max:360 },
        { p:'GURU', min:270, max:300 }, { p:'SUKR', min:150, max:180 },
        { p:'SANI', min:0,   max:30  }
    ];
    const uchhaPl  = UCHCHA_R.filter(e => { const l = siderealPositions[e.p]; return l > e.min && l < e.max; }).map(e => e.p);
    const neechaPl = NEECHA_R.filter(e => { const l = siderealPositions[e.p]; return l > e.min && l < e.max; }).map(e => e.p);
    const lUchhaPl = uchhaPl.map(p => L.PLANETS_FULL[p] || p);
    const lNeechaPl = neechaPl.map(p => L.PLANETS_FULL[p] || p);
    output.push(` ${L.EXALTATION} (UCHCHA) : ${lUchhaPl.length ? lUchhaPl.join(', ') : 'NONE'}`);
    output.push(` ${L.DEBILITATION} (NEECHA) : ${lNeechaPl.length ? lNeechaPl.join(', ') : 'NONE'}`);
    output.push('');

    // ── SHADBALA (Sthanabala status) ───────────────────────────────────────
    output.push(`                     ${L.SHADBALA}`);
    output.push('  PLANET   DEG:MN  RASI          STATUS');
    output.push('  ' + '-'.repeat(50));
    const STHANA_OWN  = { SURY:[4], CHAN:[3], KUJA:[0,7], BUDH:[2,5], GURU:[8,11], SUKR:[1,6], SANI:[9,10] };
    const STHANA_MOOL = { SURY:[4], CHAN:[3], KUJA:[0],   BUDH:[5],   GURU:[8],    SUKR:[6],   SANI:[10]   };
    for (const p of avPlanets) {
        const rasiIdx = planetRasis[p];
        const lon = siderealPositions[p];
        const degInSign = lon % 30;
        const deg = Math.floor(degInSign);
        const mn  = Math.round((degInSign - deg) * 60);
        let status = '';
        const ur = UCHCHA_R.find(e => e.p === p);
        const nr = NEECHA_R.find(e => e.p === p);
        if (ur && lon > ur.min && lon < ur.max) status = 'UCHCHA';
        else if (nr && lon > nr.min && lon < nr.max) status = 'NEECHA';
        else if ((STHANA_OWN[p]  || []).includes(rasiIdx)) status = 'SWAKSHETRA';
        else if ((STHANA_MOOL[p] || []).includes(rasiIdx)) status = 'MOOLATRIKONA';
        else {
            const lordName = RASI_LORDS[rasiIdx];
            if (FRIENDSHIP[p]) {
                const f = FRIENDSHIP[p];
                if (f.friends.includes(lordName)) status = 'MITRA KSHETRA';
                else if (f.enemies.includes(lordName)) status = 'SATRU KSHETRA';
                else status = 'SAMA KSHETRA';
            }
        }
        output.push(`  ${p.padEnd(8)} ${String(deg).padStart(2)}:${String(mn).padStart(2,'0')}   ${RASIS[rasiIdx].padEnd(13)} ${status}`);
    }
    output.push('');
    output.push('  Note: Full Shadbala (Dig/Kala/Chesta/Naisargika/Drik) requires');
    output.push('  detailed hourly ephemeris data - not computed in this version.');
    output.push('');

    // Build final output text
    const outputText = output.join('\n');

    return { outputText, planetData, siderealPositions, dasaInfo, ashtaka, bhavas, bhavaPlanets, vargasTextTable, thithi, yoga, karana, birthNak, tamilYear, tamilYearIdx: tamilYearIdx - 1, tamilMonth, tamilMonthIdx, tamilDay, vargas, navamsaPlanets };
}



export class VakyaHoroscopeService {

  constructor() {}

  async calculateHoroscope(birthDetails: BirthDetails, lang: HoroscopeLang = 'en'): Promise<HoroscopeResult> {
    try {
      console.log('Calculating horoscope for:', birthDetails, 'lang:', lang);
      
      const dateObj = new Date(birthDetails.date + 'T' + birthDetails.time);
      
      const latDeg = Math.floor(Math.abs(birthDetails.latitude));
      const latMin = Math.round((Math.abs(birthDetails.latitude) % 1) * 60);
      const latDir = birthDetails.latitude >= 0 ? 'N' : 'S';
      
      const lonDeg = Math.floor(Math.abs(birthDetails.longitude));
      const lonMin = Math.round((Math.abs(birthDetails.longitude) % 1) * 60);
      const lonDir = birthDetails.longitude >= 0 ? 'E' : 'W';

      const input = {
          name: birthDetails.name,
          day: dateObj.getDate(),
          month: dateObj.getMonth() + 1,
          year: dateObj.getFullYear(),
          hour: dateObj.getHours(),
          minute: dateObj.getMinutes(),
          timezone: birthDetails.timezone,
          place: birthDetails.place,
          latDeg: latDeg,
          latMin: latMin,
          latDir: latDir,
          lonDeg: lonDeg,
          lonMin: lonMin,
          lonDir: lonDir
      };

      const result = generateHoroscope(input, lang);
      
      const hsResult: HoroscopeResult = {
        basicInfo: {
          name: birthDetails.name,
          date: birthDetails.date,
          time: birthDetails.time,
          place: birthDetails.place,
          latitude: birthDetails.latitude,
          longitude: birthDetails.longitude,
          timezone: birthDetails.timezone
        },
        planetData: result.planetData,
        siderealPositions: result.siderealPositions,
        dasaInfo: result.dasaInfo,
        ashtaka: result.ashtaka,
        bhavas: result.bhavas,
        bhavaPlanets: result.bhavaPlanets,
        vargasTextTable: result.vargasTextTable,
        vargas: result.vargas,
        thithi: result.thithi,
        yoga: result.yoga,
        karana: result.karana,
        birthNak: result.birthNak,
        tamilYear: result.tamilYear,
        tamilYearIdx: result.tamilYearIdx,
        tamilMonth: result.tamilMonth,
        tamilMonthIdx: result.tamilMonthIdx,
        tamilDay: result.tamilDay,
        formattedOutput: result.outputText,
        htmlOutput: ''
      };

      hsResult.htmlOutput = generateCastingHTML(hsResult, lang);

      console.log('Horoscope calculation completed successfully');
      return hsResult;

    } catch (error) {
      console.error('Error calculating horoscope:', error);
      throw error;
    }
  }
}
